import type { ResidenceData } from '@repo/sdk/gen'
import { Badge } from '@repo/ui/badge'
import { Button } from '@repo/ui/button'
import { AddressSuggest } from '@repo/ui/components/address-suggest'
import { DetailSection } from '@repo/ui/components/detail-section'
import {
	Field,
	FieldDescription,
	FieldError,
	FieldGroup,
	FieldLabel
} from '@repo/ui/field'
import { Input } from '@repo/ui/input'
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue
} from '@repo/ui/select'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@repo/ui/tabs'
import { Textarea } from '@repo/ui/textarea'
import { cn } from '@repo/ui/utils'
import { Building2, Plus, Trash2 } from 'lucide-react'
import { type ReactNode, useState } from 'react'
import {
	Controller,
	useFieldArray,
	useFormContext,
	useFormState,
	useWatch
} from 'react-hook-form'
import { type TKey, useTranslation } from '@/lib/i18n'
import { useSeats } from '../-hooks/use-seats'
import {
	EMPTY_SITE,
	type ResidenceFormData,
	seatCount
} from '../-lib/residence-form'
import { RequiredMark } from './required-mark'

type Site = ResidenceData['sites'][number]
type TextKey = 'name' | 'address' | 'city'
type NullableTextKey =
	| 'bathroomsDescription'
	| 'kitchensDescription'
	| 'otherServicesDescription'
	| 'equippedBathroomsDescription'
	| 'equippedKitchensDescription'
	| 'equippedOtherServicesDescription'
type IntegerKey = 'singleBeds' | 'doubleBeds'
type NullableIntegerKey = 'bathroomsCount' | 'kitchensCount'
type EquippedKey = 'equippedSingleBeds' | 'equippedDoubleBeds'

const MAX_SITES = 3

const GOOGLE_MAPS_API_KEY: string | undefined = import.meta.env
	.VITE_GOOGLE_MAPS_API_KEY

/** The data a site must have for the call: a site missing one of them is still to fill in. */
const REQUIRED_TEXT = [
	'name',
	'address',
	'city',
	'typology',
	'bathroomsCount',
	'kitchensCount'
] as const

const toFill = (site: Site) =>
	REQUIRED_TEXT.some((key) => String(site[key] ?? '').trim() === '')

/** Two columns where the page is wide enough for them, four where the numbers sit side by side. */
const SITE_GRID = 'grid gap-x-6 gap-y-5 @xl:grid-cols-2 @3xl:grid-cols-4'

/**
 * The one to three sites of the residence, with the data section 2 of the
 * Avviso asks of each: where it is, what kind of residence, its seats, its
 * bathrooms and kitchens and what it has for students with disabilities. With
 * more than one site each is a tab with its state, and one site at a time is
 * on screen, the first with something to fill in; a refused save shows the
 * first site with an error. One site is just the form. Under the sites their
 * seats are added up against those of the residence, so a sum that does not
 * match shows before the admin saves.
 */
function SitesFields() {
	const { t } = useTranslation()
	const { control, getValues } = useFormContext<ResidenceData>()
	const { disabled, errors, submitCount } = useFormState({ control })
	const { fields, append, remove } = useFieldArray({
		control,
		name: 'sites'
	})
	const [selected, setSelected] = useState(() =>
		Math.max(0, getValues('sites').findIndex(toFill))
	)
	// Each save moves to a site with an error once, then the choice is the reader's.
	const [shownSave, setShownSave] = useState(submitCount)
	const firstWithError = fields.findIndex((_, index) => errors.sites?.[index])
	if (submitCount !== shownSave && firstWithError >= 0) {
		setShownSave(submitCount)
		setSelected(firstWithError)
	}
	const current = Math.min(selected, fields.length - 1)
	const listError = errors.sites?.root?.message ?? errors.sites?.message
	// The first site is always there: the others can go.
	const canRemove = (index: number) => index > 0 && !disabled
	const canAdd = !disabled && fields.length < MAX_SITES
	const addSite = () => {
		setSelected(fields.length)
		append(EMPTY_SITE)
	}
	const removeSite = (index: number) => {
		remove(index)
		setSelected(index - 1)
	}

	return (
		<div className='@container flex flex-col gap-6'>
			{fields.length === 1 ? (
				<DetailSection
					icon={Building2}
					title={t('admin-residences:sites.title', { number: 1 })}
				>
					<SiteFields index={0} />
				</DetailSection>
			) : (
				<Tabs
					onValueChange={(value) => setSelected(Number(value))}
					value={String(current)}
				>
					<div className='-mx-4 max-w-full overflow-x-auto overflow-y-hidden border-border border-b px-4 md:mx-0 md:px-0'>
						<TabsList variant='line'>
							{fields.map((item, index) => (
								<TabsTrigger
									key={item.id}
									value={String(index)}
								>
									<SiteTab index={index} />
								</TabsTrigger>
							))}
						</TabsList>
					</div>
					{fields.map((item, index) => (
						<TabsContent
							className='flex flex-col gap-6 pt-6'
							keepMounted
							key={item.id}
							value={String(index)}
						>
							<div className='flex flex-wrap items-center justify-between gap-3'>
								<SiteWhere index={index} />
								{canRemove(index) && (
									<Button
										aria-label={t(
											'admin-residences:sites.remove',
											{
												number: index + 1
											}
										)}
										className='text-muted-foreground hover:text-destructive'
										onClick={() => removeSite(index)}
										size='sm'
										type='button'
										variant='outline'
									>
										<Trash2 />
										{t('admin-residences:sites.remove', {
											number: index + 1
										})}
									</Button>
								)}
							</div>
							<SiteFields index={index} />
						</TabsContent>
					))}
				</Tabs>
			)}
			<SitesTotals />
			{listError && (
				<FieldError
					aria-live='polite'
					errors={[{ message: listError }]}
				/>
			)}
			{canAdd && (
				<Button
					className='self-start'
					onClick={addSite}
					type='button'
					variant='outline'
				>
					<Plus />
					{t('admin-residences:sites.add')}
				</Button>
			)}
		</div>
	)
}

/** The tab of a site: its number, its street and whether it still needs something. */
function SiteTab({ index }: { index: number }) {
	const { t } = useTranslation()
	const { control } = useFormContext<ResidenceData>()
	const { errors } = useFormState({ control })
	const site = useWatch({ control, name: `sites.${index}` })
	return (
		<>
			<span>
				{t('admin-residences:sites.title', { number: index + 1 })}
			</span>
			{site.address && (
				<span className='@3xl:inline hidden max-w-48 truncate font-normal text-muted-foreground'>
					{site.address}
				</span>
			)}
			{errors.sites?.[index] ? (
				<SiteState
					label={t('admin-residences:sites.has_errors')}
					variant='destructive'
				/>
			) : (
				toFill(site) && (
					<SiteState
						label={t('admin-residences:sites.incomplete')}
						variant='warning'
					/>
				)
			)}
		</>
	)
}

/** The state of a site in its tab: a badge where there is room, a dot on a phone. */
function SiteState({
	label,
	variant
}: {
	label: string
	variant: 'destructive' | 'warning'
}) {
	return (
		<>
			<Badge className='@xl:inline-flex hidden' variant={variant}>
				{label}
			</Badge>
			<span
				className={cn(
					'@xl:hidden size-1.5 rounded-full',
					variant === 'destructive' ? 'bg-destructive' : 'bg-warning'
				)}
			>
				<span className='sr-only'>{label}</span>
			</span>
		</>
	)
}

/** Where the site on screen is and its seats, above its fields. */
function SiteWhere({ index }: { index: number }) {
	const { t } = useTranslation()
	const { control } = useFormContext<ResidenceData>()
	const site = useWatch({ control, name: `sites.${index}` })
	const where = [site.address, site.city].filter(Boolean).join(', ')
	return (
		<p className='flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1'>
			<Building2 aria-hidden className='size-4 text-muted-foreground' />
			{where && (
				<span className='font-semibold text-[15px]'>{where}</span>
			)}
			<span className='text-muted-foreground tabular-nums'>
				{t('admin-residences:sites.summary_seats', {
					single: seatCount(site.singleBeds),
					double: seatCount(site.doubleBeds)
				})}
			</span>
		</p>
	)
}

/** The fields of a site in three groups: where it is, its rooms and services, what it has for students with disabilities. */
function SiteFields({ index }: { index: number }) {
	const { t } = useTranslation()
	return (
		<div className='flex flex-col gap-8'>
			<SiteGroup title={t('admin-residences:sites.where_title')}>
				<SiteText
					className='@3xl:col-span-2'
					index={index}
					name='name'
					required
				/>
				<SiteTypology className='@3xl:col-span-2' index={index} />
				<SiteAddress
					className='@3xl:col-span-3'
					index={index}
					required
				/>
				<SiteText index={index} name='city' required />
			</SiteGroup>
			<SiteGroup title={t('admin-residences:sites.rooms_title')}>
				<SiteNumber index={index} name='singleBeds' required />
				<SiteNumber index={index} name='doubleBeds' required step={2} />
				<SiteNumber
					index={index}
					name='bathroomsCount'
					nullable
					required
				/>
				<SiteNumber
					index={index}
					name='kitchensCount'
					nullable
					required
				/>
				<SiteNullableText
					className='@3xl:col-span-2'
					index={index}
					multiline
					name='bathroomsDescription'
				/>
				<SiteNullableText
					className='@3xl:col-span-2'
					index={index}
					multiline
					name='kitchensDescription'
				/>
				<SiteNullableText
					className='@3xl:col-span-4 @xl:col-span-2'
					index={index}
					multiline
					name='otherServicesDescription'
				/>
			</SiteGroup>
			<SiteGroup title={t('admin-residences:sites.equipped_title')}>
				<SiteNumber index={index} name='equippedSingleBeds' required />
				<SiteNumber
					index={index}
					name='equippedDoubleBeds'
					required
					step={2}
				/>
				<SiteNullableText
					className='@xl:col-span-2'
					index={index}
					multiline
					name='equippedBathroomsDescription'
				/>
				<SiteNullableText
					className='@xl:col-span-2'
					index={index}
					multiline
					name='equippedKitchensDescription'
				/>
				<SiteNullableText
					className='@xl:col-span-2'
					index={index}
					multiline
					name='equippedOtherServicesDescription'
				/>
			</SiteGroup>
		</div>
	)
}

function SiteGroup({
	title,
	children
}: {
	title: string
	children: ReactNode
}) {
	return (
		<section className='flex flex-col gap-4'>
			<h4 className='border-border border-b pb-2 font-medium text-sm'>
				{title}
			</h4>
			<FieldGroup className={SITE_GRID}>{children}</FieldGroup>
		</section>
	)
}

/** The seats of all the sites: the seats of the residence, which Posti e tariffe divides between the quotas. */
function SitesTotals() {
	const { t } = useTranslation()
	const { single, double } = useSeats()
	return (
		<p
			aria-live='polite'
			className='text-muted-foreground text-sm tabular-nums'
		>
			{t('admin-residences:sites.totals', { single, double })}
		</p>
	)
}

function SiteShell({
	id,
	labelKey,
	description,
	className,
	required = false,
	error,
	children
}: {
	id: string
	labelKey: TKey
	description?: ReactNode
	className?: string
	/** Marks a datum the call is written from. */
	required?: boolean
	error?: { message?: string }
	children: ReactNode
}) {
	const { t } = useTranslation()
	return (
		<Field className={className} data-invalid={error !== undefined}>
			<FieldLabel className={required ? 'gap-1' : undefined} htmlFor={id}>
				{t(labelKey)}
				{required && <RequiredMark />}
			</FieldLabel>
			{children}
			{description && <FieldDescription>{description}</FieldDescription>}
			{error && <FieldError errors={[error]} />}
		</Field>
	)
}

const labelOf = (key: Exclude<keyof Site, 'id'>) =>
	`admin-residences:site_fields.${key}` as const satisfies TKey

function SiteText({
	index,
	name,
	className,
	required
}: {
	index: number
	name: TextKey
	className?: string
	required?: boolean
}) {
	const { control } = useFormContext<ResidenceData>()
	const id = `residence-sites-${index}-${name}`
	return (
		<Controller
			control={control}
			name={`sites.${index}.${name}`}
			render={({ field, fieldState }) => (
				<SiteShell
					className={className}
					error={fieldState.error}
					id={id}
					labelKey={labelOf(name)}
					required={required}
				>
					<Input
						{...field}
						aria-invalid={fieldState.invalid}
						id={id}
					/>
				</SiteShell>
			)}
		/>
	)
}

/**
 * The street of the site, typed freely with the suggestions of Google under it: the one picked
 * gives the street, the municipality and where the site is on the map. An address typed by hand
 * is not a place Google named, so it takes the site off the map until one is picked.
 */
function SiteAddress({
	index,
	className,
	required
}: {
	index: number
	className?: string
	required?: boolean
}) {
	const { t, i18n } = useTranslation()
	const { control, setValue } = useFormContext<ResidenceFormData>()
	const { disabled } = useFormState({ control })
	const located = useWatch({ control, name: `sites.${index}.location` })
	const id = `residence-sites-${index}-address`
	return (
		<Controller
			control={control}
			name={`sites.${index}.address`}
			render={({ field, fieldState }) => (
				<SiteShell
					className={className}
					description={
						!(located || disabled || !GOOGLE_MAPS_API_KEY) &&
						t('admin-residences:hints.address_position')
					}
					error={fieldState.error}
					id={id}
					labelKey={labelOf('address')}
					required={required}
				>
					<AddressSuggest
						apiKey={GOOGLE_MAPS_API_KEY}
						aria-invalid={fieldState.invalid}
						attribution={t(
							'admin-residences:hints.address_attribution'
						)}
						id={id}
						language={i18n.language}
						name={field.name}
						onBlur={field.onBlur}
						onPick={(address) => {
							const street = [address.street, address.number]
								.filter(Boolean)
								.join(' ')
							if (street) {
								setValue(`sites.${index}.address`, street, {
									shouldDirty: true
								})
							}
							if (address.locality) {
								setValue(
									`sites.${index}.city`,
									address.locality,
									{ shouldDirty: true }
								)
							}
							setValue(
								`sites.${index}.location`,
								address.location,
								{
									shouldDirty: true
								}
							)
						}}
						onValueChange={(text) => {
							field.onChange(text)
							setValue(`sites.${index}.location`, null, {
								shouldDirty: true
							})
						}}
						placeholder={t(
							'admin-residences:site_placeholders.address'
						)}
						ref={field.ref}
						value={field.value ?? ''}
						withLocation
					/>
				</SiteShell>
			)}
		/>
	)
}

/** Optional text: an empty input is `null`, never an empty string. */
function SiteNullableText({
	index,
	name,
	multiline = false,
	className,
	required
}: {
	index: number
	name: NullableTextKey
	multiline?: boolean
	className?: string
	required?: boolean
}) {
	const { t } = useTranslation()
	const { control } = useFormContext<ResidenceData>()
	const hint = SITE_HINT[name]
	const placeholder = SITE_PLACEHOLDER[name]
	const id = `residence-sites-${index}-${name}`
	return (
		<Controller
			control={control}
			name={`sites.${index}.${name}`}
			render={({ field, fieldState }) => {
				const controlProps = {
					ref: field.ref,
					name: field.name,
					disabled: field.disabled,
					onBlur: field.onBlur,
					id,
					placeholder: placeholder && t(placeholder),
					'aria-invalid': fieldState.invalid,
					value: field.value ?? '',
					onChange: (event: { target: { value: string } }) =>
						field.onChange(
							event.target.value.trim() === ''
								? null
								: event.target.value
						)
				}
				return (
					<SiteShell
						className={className}
						description={hint && t(hint)}
						error={fieldState.error}
						id={id}
						labelKey={labelOf(name)}
						required={required}
					>
						{multiline ? (
							<Textarea
								{...controlProps}
								className='min-h-14'
								rows={2}
							/>
						) : (
							<Input {...controlProps} />
						)}
					</SiteShell>
				)
			}}
		/>
	)
}

/**
 * The typology of the site, chosen from those the Allegato C of the standards
 * (D.M. 1437/2022, recalled by D.M. 481/2024) names, or written when it is
 * another: the Allegato lists them "a titolo esemplificativo" and allows
 * others. The value is the words the call prints after "Tipologia:", so a
 * saved value outside the list reads as "Altra" with its text.
 */
function SiteTypology({
	index,
	className
}: {
	index: number
	className?: string
}) {
	const { control } = useFormContext<ResidenceData>()
	const id = `residence-sites-${index}-typology`
	return (
		<Controller
			control={control}
			name={`sites.${index}.typology`}
			render={({ field, fieldState }) => (
				<SiteShell
					className={className}
					error={fieldState.error}
					id={id}
					labelKey='admin-residences:site_fields.typology'
					required
				>
					<TypologyControl
						disabled={field.disabled}
						id={id}
						invalid={fieldState.invalid}
						onBlur={field.onBlur}
						onChange={field.onChange}
						value={field.value}
					/>
				</SiteShell>
			)}
		/>
	)
}

/** The select, and the text of "Altra" under it once chosen. */
function TypologyControl({
	id,
	value,
	onChange,
	onBlur,
	disabled,
	invalid
}: {
	id: string
	value: string | null
	onChange: (value: string | null) => void
	onBlur: () => void
	disabled?: boolean
	invalid: boolean
}) {
	const { t } = useTranslation()
	const listed = TYPOLOGIES.find((item) => item.value === value)
	// "Altra" stays chosen while its text is still empty.
	const [other, setOther] = useState(value !== null && !listed)
	// "Altra" wins over the list: text typed in it that matches a listed value stays in the box.
	const choice = other ? OTHER : (listed?.value ?? null)
	return (
		<div className='flex flex-col gap-2'>
			<Select
				disabled={disabled}
				onValueChange={(next: string | null) => {
					setOther(next === OTHER)
					onChange(next === OTHER || next === null ? null : next)
				}}
				value={choice}
			>
				<SelectTrigger
					aria-invalid={invalid}
					className='w-full'
					id={id}
				>
					<SelectValue>
						{(picked: string | null) =>
							picked === null
								? t('admin-residences:typology.choose')
								: // "Altra" is not in the list, so it falls to its own label.
									t(
										TYPOLOGIES.find(
											(item) => item.value === picked
										)?.label ??
											'admin-residences:typology.other'
									)
						}
					</SelectValue>
				</SelectTrigger>
				<SelectContent>
					{TYPOLOGIES.map((item) => (
						<SelectItem key={item.value} value={item.value}>
							{t(item.label)}
						</SelectItem>
					))}
					<SelectItem value={OTHER}>
						{t('admin-residences:typology.other')}
					</SelectItem>
				</SelectContent>
			</Select>
			{choice === OTHER && (
				<Input
					aria-invalid={invalid}
					aria-label={t('admin-residences:typology.other')}
					disabled={disabled}
					onBlur={onBlur}
					onChange={(event) =>
						onChange(
							event.target.value.trim() === ''
								? null
								: event.target.value
						)
					}
					placeholder={t(
						'admin-residences:typology.other_placeholder'
					)}
					value={value ?? ''}
				/>
			)}
		</div>
	)
}

/** The typologies the Allegato C names, each value as the call prints it. */
const TYPOLOGIES = [
	{ value: 'ad albergo', label: 'admin-residences:typology.hotel' },
	{
		value: 'a minialloggi',
		label: 'admin-residences:typology.mini_apartments'
	},
	{
		value: 'a nuclei integrati',
		label: 'admin-residences:typology.integrated_units'
	},
	{ value: 'mista', label: 'admin-residences:typology.mixed' }
] as const satisfies readonly { value: string; label: TKey }[]

/** The select's value for "Altra": never saved, the text under it is. */
const OTHER = '__other__'

/** A whole number; with `nullable` an empty input is `null`, without it an empty input is not a number and the schema says so. */
function SiteNumber({
	index,
	name,
	nullable = false,
	className,
	required,
	step = 1
}: {
	index: number
	name: IntegerKey | EquippedKey | NullableIntegerKey
	nullable?: boolean
	className?: string
	required?: boolean
	step?: number
}) {
	const { control } = useFormContext<ResidenceData>()
	const id = `residence-sites-${index}-${name}`
	return (
		<Controller
			control={control}
			name={`sites.${index}.${name}`}
			render={({ field, fieldState }) => (
				<SiteShell
					className={className}
					error={fieldState.error}
					id={id}
					labelKey={labelOf(name)}
					required={required}
				>
					<Input
						aria-invalid={fieldState.invalid}
						className='tabular-nums'
						disabled={field.disabled}
						id={id}
						inputMode='numeric'
						min={0}
						name={field.name}
						onBlur={field.onBlur}
						onChange={(event) =>
							field.onChange(
								nullable && event.target.value === ''
									? null
									: event.target.valueAsNumber
							)
						}
						ref={field.ref}
						step={step}
						type='number'
						value={
							field.value === null || Number.isNaN(field.value)
								? ''
								: field.value
						}
					/>
				</SiteShell>
			)}
		/>
	)
}

/** An example of what to write, the way section 2 of the Avviso reads it. */
const SITE_PLACEHOLDER: Partial<Record<NullableTextKey, TKey>> = {
	bathroomsDescription:
		'admin-residences:site_placeholders.bathrooms_description',
	kitchensDescription:
		'admin-residences:site_placeholders.kitchens_description'
}

/** What the call does with a field left empty: a rule, so it stays on the page. */
const SITE_HINT: Partial<Record<NullableTextKey, TKey>> = {
	otherServicesDescription: 'admin-residences:site_hints.other_services'
}

/**
 * The address and city of the first site, asked with the residence's own
 * data: the page that shows them starts the form with one site when the
 * residence has none yet.
 */
function FirstSiteFields() {
	return (
		<>
			<SiteAddress index={0} required />
			<SiteText index={0} name='city' required />
		</>
	)
}

export { FirstSiteFields, SitesFields }
